import {
  Box,
  Divider,
  Flex,
  Image,
  Picture,
  Typography,
} from "@jewlr/storybook/core"
import PropTypes from "prop-types"
import React from "react"
import { Link } from "react-router-dom"
import styled from "styled-components"

import InstantGiftExplainedModal from "areas/instant-gift/explained/modal"
import { openModal } from "components/modal"
import { IS_AU, IS_CA, IS_UK, IS_US, jewlr_t } from "helpers/application"
import {
  showEarlyShippingInfo,
  showHolidayShippingInfo,
  holidayCTALink,
  isInCutoffPeriod,
} from "helpers/holiday"
import { SectionLink } from "jewlr/pages/components"

import { PageTitle, Paragraph } from "./components"

const Table = styled.table`
  border-collapse: separate;
  border-spacing: 0 1em;
  table-layout: fixed;
  text-align: left;
  width: 100%;
  ${(props) => props.theme.mediaQueries.desktop`
    padding-left: 16px;
    padding-right: 16px;
  `}
`
const TableData = styled.td`
  font-size: 16px;
  font-weight: 300;
  letter-spacing: -0.02em;
  line-height: 22px;
  text-align: center;
  vertical-align: top;
  width: calc(100% / 3);
  &:not(:first-child):not(:last-child) {
    padding: 0 8px;
  }
`
const TableHead = styled.th`
  color: ${(props) => props.theme.colors.black};
  font-size: 18px;
  font-weight: 500;
  letter-spacing: -0.02em;
  line-height: 22px;
  text-align: center;
  vertical-align: top;
  width: calc(100% / 3);
  &:not(:first-child):not(:last-child) {
    padding: 0 8px;
  }
`
const Title = styled(Typography).attrs((props) => ({
  as: "h2",
  fontSize: 26,
  fontWeight: 500,
  lineHeight: "30px",
  mb: 2,
  textAlign: "center",
  textTransform: "capitalize",
  ...props,
}))``

const InstantBox = styled(Box)`
  ${({ theme }) => `
    background-color: ${theme.colors.grey[10]};
  `}
`

const Strong = styled.strong.attrs((props) => ({
  ...props,
}))`
  font-weight: 500;
`

const ShippingInformation = () => {
  return (
    <Box contain px={{ _: "34px", tablet: 2 }}>
      <Box pt={4} textAlign="center" width="100%">
        <PageTitle>{jewlr_t("terms.Shipping")} Information</PageTitle>

        <Box mb={5}>
          {showHolidayShippingInfo() && (
            <>
              {isInCutoffPeriod() ? (
                <>
                  <Title color="sale" textTransform="uppercase">
                    Shop Now for {jewlr_t("terms.Personalized")} Gifts!
                  </Title>
                  <Paragraph>
                    Order today &ndash; it&apos;s never too late! We start
                    handcrafting your {jewlr_t("terms.jewelry")} right away, and
                    with our{" "}
                    <Typography as={Link} to="/info/instant_gift" underline>
                      Instant Gift
                    </Typography>{" "}
                    video, you can send your gift virtually to a loved one. Your
                    actual gift will arrive soon after{" "}
                    {sl.config.guaranteed_holiday}.
                  </Paragraph>
                </>
              ) : (
                <>
                  <Title color="sale" textTransform="uppercase">
                    {showEarlyShippingInfo() ? "SHOP EARLY" : "ORDER TODAY"} FOR{" "}
                    {sl.config.guaranteed_holiday} DELIVERY!
                  </Title>
                  <Paragraph>
                    Shop now to avoid delays and receive your{" "}
                    {sl.config.guaranteed_holiday} gifts on time.
                  </Paragraph>
                  <Paragraph>
                    {showEarlyShippingInfo()
                      ? "All items are"
                      : "We still have items"}{" "}
                    available for delivery by {sl.config.guaranteed_holiday}.{" "}
                    {jewlr_t("terms.Personalize")} your item and view the
                    available delivery dates for your item and location.
                  </Paragraph>
                  <SectionLink
                    data-lc="shipping-information"
                    display="inline-block"
                    mt={1}
                    to={holidayCTALink()}
                  >
                    Shop Now
                  </SectionLink>
                </>
              )}
              <Divider bg="grey.10" my="46px" />
            </>
          )}

          <Title>Estimated Delivery & Manufacturing Time</Title>
          <Paragraph>
            At Jewlr, each item is made-to-order and takes between 1 and 4 days
            to manufacture. We calculate the estimated delivery dates on the
            item page using average manufacturing times and available{" "}
            {jewlr_t("terms.shipping")} methods. Your order confirmation email
            will also include the estimated delivery date.
          </Paragraph>

          <Divider bg="grey.10" my="46px" />

          {IS_US() && (
            <>
              <Title>FREE {jewlr_t("terms.Shipping")} To All 50 States</Title>
              <Paragraph>
                We offer free standard {jewlr_t("terms.shipping")} on all items
                to all states &ndash; including Alaska and Hawaii. For faster{" "}
                {jewlr_t("terms.shipping")} service, upgrade to one of our
                Express {jewlr_t("terms.Shipping")} options.
              </Paragraph>
              <Paragraph>
                <Strong>Note:</Strong> These delivery times do{" "}
                <Strong>not</Strong> include manufacturing time (see above). We
                ship using UPS, FedEx, and USPS. Please allow additional
                delivery time if shipping to APOs, FPOs, PO boxes or remote
                locations.{" "}
              </Paragraph>
              <Box mb={3}>
                <Image
                  alt="UPS"
                  mr={3}
                  ratio="182 / 216"
                  src={require("images/info/shipping_information/ups.svg")}
                  width="80px"
                />
                <Image
                  alt="FedEx"
                  mr={3}
                  ratio="366 / 178"
                  src={require("images/info/shipping_information/fedex.png")}
                  width="135px"
                />
                <Image
                  alt="USPS"
                  ratio="238 / 201"
                  src={require("images/info/shipping_information/usps.png")}
                  width="80px"
                />
              </Box>
              <Divider bg="grey.10" my="46px" />
              <Title mb="28px">
                Delivery Times and <Box as="br" display={{ tablet: "none" }} />{" "}
                {jewlr_t("terms.Shipping")} Rates
              </Title>
              <Table>
                <thead>
                  <tr>
                    <TableHead>{jewlr_t("terms.Shipping")} Service</TableHead>
                    <TableHead>Delivery Time</TableHead>
                    <TableHead>Rates</TableHead>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <TableData>Standard Ground</TableData>
                    <TableData>3&ndash;4 Business Days</TableData>
                    <TableData>
                      <Strong>FREE</Strong>
                    </TableData>
                  </tr>

                  <tr>
                    <TableData>Expedited</TableData>
                    <TableData>2 Business Days</TableData>
                    <TableData>
                      <strike>$19</strike> $12
                    </TableData>
                  </tr>
                  <tr>
                    <TableData>Express</TableData>
                    <TableData>1 Business Day</TableData>
                    <TableData>
                      <strike>$25</strike> $17
                    </TableData>
                  </tr>
                </tbody>
              </Table>
            </>
          )}
          {IS_CA() && (
            <>
              <Title>FREE {jewlr_t("terms.Shipping")} Within Canada</Title>
              <Paragraph>
                We offer <Strong>free</Strong> standard{" "}
                {jewlr_t("terms.shipping")} on all items. For faster{" "}
                {jewlr_t("terms.shipping")} service, upgrade to one of our
                Express {jewlr_t("terms.Shipping")} options.
              </Paragraph>
              <Paragraph>
                <Strong>Note:</Strong> These delivery times do{" "}
                <Strong>not</Strong> include the manufacturing time (see above).
                We ship using Canada Post, UPS and FedEx. Please allow
                additional delivery time if {jewlr_t("terms.shipping")} to PO
                Boxes or remote locations.{" "}
              </Paragraph>
              <Divider bg="grey.10" my={3} />
              <Title mb="28px">
                Delivery Times And <Box as="br" display={{ tablet: "none" }} />{" "}
                {jewlr_t("terms.Shipping")} Rates
              </Title>
              <Table>
                <thead>
                  <tr>
                    <TableHead>{jewlr_t("terms.Shipping")} Service</TableHead>
                    <TableHead>Delivery Time</TableHead>
                    <TableHead>Rates</TableHead>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <TableData>
                      Expedited Parcel{" "}
                      <Box as="br" display={{ tablet: "none" }} />
                    </TableData>
                    <TableData>2&ndash;5 Business Days</TableData>
                    <TableData>
                      <Strong>FREE with every order</Strong>
                    </TableData>
                  </tr>

                  <tr>
                    <TableData>Express or Priority</TableData>
                    <TableData>1&ndash;2 Business Days</TableData>
                    <TableData>Depends on your postal code</TableData>
                  </tr>
                </tbody>
              </Table>
            </>
          )}
          {IS_UK() && (
            <>
              <Title>FREE Delivery To The UK</Title>
              <Paragraph>
                We are pleased to offer <Strong>free</Strong> standard delivery
                on all items to the UK.
              </Paragraph>
              <Paragraph>
                All {jewlr_t("terms.jewelry")} orders are handcrafted in our
                workshop in Canada. Once complete, your order is delivered via
                expedited courier service to the United Kingdom.
              </Paragraph>
              <Paragraph>
                <Strong>Note:</Strong> {jewlr_t("terms.Shipping")} times do not
                include the manufacturing time (see above). Occasionally we
                encounter delays that affect our quoted delivery dates, such as
                extreme weather, customs clearance, or inaccurate addresses.
                These delays are beyond our control.
              </Paragraph>
              <Divider bg="grey.10" my="46px" />
              <Title mb="28px">Delivery Times and Rates</Title>
              <Table>
                <thead>
                  <tr>
                    <TableHead>{jewlr_t("terms.Shipping")}</TableHead>
                    <TableHead>Delivery Time</TableHead>
                    <TableHead>Rates</TableHead>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <TableData>Standard Delivery</TableData>
                    <TableData>4&ndash;5 Working Days</TableData>
                    <TableData>
                      <Strong>FREE with every order</Strong>
                    </TableData>
                  </tr>

                  <tr>
                    <TableData>Express Delivery</TableData>
                    <TableData>3 Working Days</TableData>
                    <TableData>Provided at checkout</TableData>
                  </tr>
                </tbody>
              </Table>
            </>
          )}
          {IS_AU() && (
            <>
              <Title>FREE {jewlr_t("terms.Shipping")} To Australia</Title>
              <Paragraph>
                We are pleased to offer <Strong>free</Strong> shipping on all
                items to Australia.{" "}
              </Paragraph>
              <Paragraph>
                All {jewlr_t("terms.jewelry")} orders are handcrafted in our
                workshop in Canada. Once complete, your order is shipped to our
                Australian office, and from there, the Australia Post Express
                Post Service will deliver it to you.
              </Paragraph>
              <Paragraph>
                <Strong>Note:</Strong> {jewlr_t("terms.Shipping")} times do not
                include the manufacturing time (see above). Occasionally we
                encounter delays that affect our quoted delivery dates, such as
                extreme weather, customs clearance, or inaccurate addresses.
                These delays are beyond our control.
              </Paragraph>
              <Divider bg="grey.10" my="46px" />
              <Title mb="28px">
                {jewlr_t("terms.Shipping")} Times and Rates
              </Title>
              <Flex
                fontSize="16px"
                fontWeight="light"
                justifyContent="space-around"
                letterSpacing="-0.02em"
                py="10px"
              >
                <Typography flexBasis="33%">
                  Expedited {jewlr_t("terms.Shipping")}
                </Typography>
                <Typography flexBasis="33%">5&ndash;7 Working Days</Typography>
                <Typography as="strong" flexBasis="33%" fontWeight={500}>
                  FREE with every order
                </Typography>
              </Flex>
            </>
          )}

          <Divider bg="grey.10" my="46px" />

          <Title>Instant Gift</Title>
          <Paragraph fontSize={20} lineHeight="20px" mb="46px">
            Need this gift tomorrow? Give it virtually!
          </Paragraph>
          <InstantBox
            display={{ _: "", tablet: "flex" }}
            mb={8}
            mx={{ _: "-24px", tablet: 0 }}
            pb={{ _: 7, tablet: 0 }}
            px={{ _: 3, desktop: 6 }}
          >
            <Flex alignItems="flex-end" flex="1" mb={{ _: 2, tablet: 0 }}>
              <Picture
                alt="Instant Gift"
                fallback={require("images/instant-gifts/shipping-information/instant-gift.png")}
                srcSet={[
                  {
                    height: "179",
                    media: "(max-width: 767px)",
                    sizes: "100vw",
                    srcSet: `
                      ${require("images/instant-gifts/shipping-information/m-instant-gift.png")} 260w,
                      ${require("images/instant-gifts/shipping-information/m-instant-gift-2x.png")} 520w
                    `,
                    width: "260",
                  },
                  {
                    height: "410",
                    media: "(min-width: 768px)",
                    sizes: "(max-width: 1023px) 272px, 416px",
                    srcSet: `
                      ${require("images/instant-gifts/shipping-information/instant-gift.png")} 547w,
                      ${require("images/instant-gifts/shipping-information/instant-gift-2x.png")} 1094w,
                    `,
                    width: "547",
                  },
                ]}
              />
            </Flex>
            <Flex
              flex="1"
              flexDirection="column"
              justifyContent="center"
              px={{ tablet: 4 }}
              py={{ tablet: 3 }}
            >
              <Title as="h3" fontSize={20} lineHeight="24px" mb="10px">
                What is an Instant Gift?
              </Title>
              <Paragraph fontSize={16} mb="22px">
                The Instant Gift page features a personalized video that reveals
                your one-of-a-kind gift to your loved one, as well as item
                details and delivery date. We begin handcrafting your order
                right away and it will arrive shortly after.
              </Paragraph>
              <SectionLink
                as="span"
                onClick={() => {
                  openModal("#instant-gift-explained")
                }}
                pointer
              >
                Learn More
              </SectionLink>
              <InstantGiftExplainedModal />
            </Flex>
          </InstantBox>
        </Box>
      </Box>
    </Box>
  )
}

ShippingInformation.propTypes = {
  toggleInstantGiftExplained: PropTypes.func,
}
export default ShippingInformation
